import { createFileRoute } from '@tanstack/react-router';
import { cacheHeadersFor, experimentalRoutes } from '@/analytics';
import { MARKETING_CACHE_CONTROL } from '@/analytics/cache-headers';
import { FAQButton } from '@/components/astro/new-design-25/ui/FAQ-Button';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import { NavBar } from '@/components/astro/new-design-25/ui/NavBar';
import { PricingExperimentSelector } from '@/components/web-fe/pricing/pricing-experiment-selector';
import { PricingPlans } from '@/components/web-fe/pricing/pricing-plans';
import { PricingPlatforms } from '@/components/web-fe/pricing/pricing-platforms';
import { useFreePlan } from '@/hooks/use-free-plan-availability';
import { useMarketingPricingArm } from '@/hooks/use-marketing-pricing-arm';
import * as m from '@/paraglide/messages';
import { hreflangLinks, seo } from '@/seo/seo';
import { marketingPricingForArm } from '@/server/services/pricing-service';

const ANALYTICS = experimentalRoutes['/_marketing/pricing'];

function PricingPage() {
  const { isInFreePlanCountry, isResolvingCountry } = useFreePlan();
  const pricing = marketingPricingForArm(useMarketingPricingArm());
  return (
    <div className='pricing-theme bg-(--pricing-paper)'>
      <NavBar />
      <div className='font-(family-name:--pricing-font) leading-[normal] text-(--pricing-ink)'>
        <main id='main-content' tabIndex={-1}>
          <PricingExperimentSelector experimentNames={ANALYTICS.experiments} />
          <PricingPlans
            pricing={pricing}
            showFreePlan={isInFreePlanCountry}
            freePlanPending={isResolvingCountry}
          />
          <PricingPlatforms />
          <FAQButton />
        </main>
        <Footer />
      </div>
    </div>
  );
}

/**
 * `/pricing` — `cdn-shared` since `pricing-test-v1`: the cache key carries the
 * arm (`x-variants`) so the page can show the price the visitor will be quoted.
 *
 * `MARKETING_CACHE_CONTROL` keeps the `_marketing` layout's TTL
 * (`s-maxage=3600` + `stale-if-error`) rather than `cacheHeadersFor`'s shorter
 * default — the per-arm entries need the longer window to stay warm.
 */
export const Route = createFileRoute('/_marketing/pricing')({
  staticData: { analytics: ANALYTICS },
  headers: cacheHeadersFor(ANALYTICS, MARKETING_CACHE_CONTROL),
  head: () => ({
    meta: [
      ...seo({
        title: m.meta_pricing_title(),
        description: m.meta_pricing_description(),
      }),
    ],
    links: [...hreflangLinks('/pricing')],
  }),
  component: () => (
    <>
      <a
        className='sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4'
        href='#main-content'
      >
        {m.skip_to_main_content()}
      </a>
      <PricingPage />
    </>
  ),
});
